import { OptimizerRunV2State } from '@generated/graphql';
import { Box, LinearProgress, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { BulkRebalanceRun } from '../../types/run.types';
import {
  getProcessedPortfolioCount,
  getRunProgressValue,
} from '../../utils/presentation/runProgress';

export type BulkRebalanceRunProgressPanelProps = {
  run: BulkRebalanceRun;
  previewReportCount: number;
};

const PROGRESS_STATE_KEYS: Partial<Record<OptimizerRunV2State, string>> = {
  [OptimizerRunV2State.Initiated]: 'detail.progressStateDescription.INITIATED',
  [OptimizerRunV2State.Optimizing]:
    'detail.progressStateDescription.OPTIMIZING',
  [OptimizerRunV2State.Validating]:
    'detail.progressStateDescription.VALIDATING',
  [OptimizerRunV2State.Publishing]:
    'detail.progressStateDescription.PUBLISHING',
};

export const BulkRebalanceRunProgressPanel = ({
  run,
  previewReportCount,
}: BulkRebalanceRunProgressPanelProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const summary = run.portfolioSummary;
  const processedCount = getProcessedPortfolioCount(summary);
  const progressKey = PROGRESS_STATE_KEYS[run.state];

  return (
    <Box
      data-testid='bulk-rebalance-run-progress-panel'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        border: '1px solid',
        borderColor: theme.color.outlineVariant,
        borderRadius: theme.borderRadius.md,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='bodyMedium'>
          {t('detail.progressLabel')}
        </Typography>
        {progressKey && (
          <Typography variant='bodySmall' color='textSecondary'>
            {t(progressKey)}
          </Typography>
        )}
      </Box>

      <LinearProgress
        variant='determinate'
        value={getRunProgressValue(run.state, summary)}
        data-testid='bulk-rebalance-run-progress'
      />

      {summary && summary.total > 0 && (
        <Typography variant='bodySmall' color='textSecondary'>
          {t('detail.progressProcessedCount', {
            processed: processedCount,
            total: summary.total,
          })}
        </Typography>
      )}

      {previewReportCount > 0 ? (
        <Typography
          variant='bodySmall'
          data-testid='bulk-rebalance-progress-preview-count'
        >
          {t('detail.progressPreviewCount', { count: previewReportCount })}
        </Typography>
      ) : (
        <Typography variant='bodySmall' color='textSecondary'>
          {t('detail.progressPollingNotice')}
        </Typography>
      )}
    </Box>
  );
};
